import { differenceInCalendarDays, format, isValid, parseISO } from "date-fns";
import type { TFunction } from "i18next";

import {
  BID_REQUEST_STATUS,
  BID_STATUS,
  FULFILMENT_STATUS,
  INVITATION_RESPONSE_STATUS,
  type BidRequest,
  type BidRequestItem,
  type BidRequestStatus,
  type Currency,
  type FulfilmentStatus,
  type SchoolBid,
  type Settlement,
  type SettlementCommodity,
} from "./types";

export type BadgeVariant =
  | "default"
  | "secondary"
  | "outline"
  | "destructive"
  | "warning"
  | "success"
  | "ghost"
  | "link";

export const BID_REQUEST_STATUS_BADGE_VARIANT: Record<BidRequestStatus, BadgeVariant> = {
  [BID_REQUEST_STATUS.openToBids]: "secondary",
  [BID_REQUEST_STATUS.biddingComplete]: "default",
  [BID_REQUEST_STATUS.acceptedBid]: "success",
  [BID_REQUEST_STATUS.cancelled]: "destructive",
};

export const BID_REQUEST_STATUS_LABEL_KEY: Record<BidRequestStatus, string> = {
  [BID_REQUEST_STATUS.openToBids]: "bidRequests.status.openToBids",
  [BID_REQUEST_STATUS.biddingComplete]: "bidRequests.status.biddingComplete",
  [BID_REQUEST_STATUS.acceptedBid]: "bidRequests.status.acceptedBid",
  [BID_REQUEST_STATUS.cancelled]: "bidRequests.status.cancelled",
};

export const getStatusBadgeVariant = (status: BidRequestStatus): BadgeVariant =>
  BID_REQUEST_STATUS_BADGE_VARIANT[status] ?? "secondary";

export const getStatusLabel = (status: BidRequestStatus, t: TFunction): string => {
  const key = BID_REQUEST_STATUS_LABEL_KEY[status];
  return key ? t(key) : status;
};

export const formatBidRequestDate = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  const date = parseISO(value);
  return isValid(date) ? format(date, "d MMM yyyy") : "";
};

export const getDaysLeft = (postingEndDate?: string | null, from: Date = new Date()): number => {
  if (!postingEndDate) {
    return 0;
  }
  const end = parseISO(postingEndDate);
  if (!isValid(end)) {
    return 0;
  }
  return Math.max(differenceInCalendarDays(end, from), 0);
};

export const getSubmittedCount = (bidRequest: BidRequest): number =>
  bidRequest.invitations.filter(
    invitation => invitation.response_status === INVITATION_RESPONSE_STATUS.submitted
  ).length;

export const getCommodityNames = (bidRequest: BidRequest): string =>
  bidRequest.items
    .map(item => item.commodity_name)
    .filter(Boolean)
    .join(", ");

export const getBiddingPeriodLabel = (
  bidRequest: Pick<BidRequest, "posting_start_date" | "posting_end_date">
): string => {
  const start = formatBidRequestDate(bidRequest.posting_start_date);
  const end = formatBidRequestDate(bidRequest.posting_end_date);
  return `${start} – ${end}`;
};

export const formatAmount = (amount: string | number): string => {
  const value = Number(amount);
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
};

export const formatMoney = (currency: Currency | null, amount: string | number): string => {
  const formatted = formatAmount(amount);
  const prefix = currency?.symbol ?? currency?.code;
  return prefix ? `${prefix} ${formatted}` : formatted;
};

export const getWinningBid = (bids: SchoolBid[]): SchoolBid | undefined =>
  bids.find(bid => bid.status === BID_STATUS.won);

// --- Part 2: delivery + payment settlement ---------------------------------

export const FULFILMENT_STATUS_BADGE_VARIANT: Record<FulfilmentStatus, BadgeVariant> = {
  [FULFILMENT_STATUS.awaitingAcceptance]: "outline",
  [FULFILMENT_STATUS.awaitingStock]: "outline",
  [FULFILMENT_STATUS.readyForDelivery]: "secondary",
  [FULFILMENT_STATUS.partiallyDelivered]: "warning",
  [FULFILMENT_STATUS.delivered]: "success",
  [FULFILMENT_STATUS.pendingPayment]: "warning",
  [FULFILMENT_STATUS.paid]: "success",
  [FULFILMENT_STATUS.cancelled]: "destructive",
};

export const FULFILMENT_STATUS_LABEL_KEY: Record<FulfilmentStatus, string> = {
  [FULFILMENT_STATUS.awaitingAcceptance]: "bidRequests.fulfilment.awaitingAcceptance",
  [FULFILMENT_STATUS.awaitingStock]: "bidRequests.fulfilment.awaitingStock",
  [FULFILMENT_STATUS.readyForDelivery]: "bidRequests.fulfilment.readyForDelivery",
  [FULFILMENT_STATUS.partiallyDelivered]: "bidRequests.fulfilment.partiallyDelivered",
  [FULFILMENT_STATUS.delivered]: "bidRequests.fulfilment.delivered",
  [FULFILMENT_STATUS.pendingPayment]: "bidRequests.fulfilment.pendingPayment",
  [FULFILMENT_STATUS.paid]: "bidRequests.fulfilment.paid",
  [FULFILMENT_STATUS.cancelled]: "bidRequests.fulfilment.cancelled",
};

export const getFulfilmentBadgeVariant = (status: FulfilmentStatus): BadgeVariant =>
  FULFILMENT_STATUS_BADGE_VARIANT[status] ?? "secondary";

export const getFulfilmentLabel = (status: FulfilmentStatus, t: TFunction): string => {
  const key = FULFILMENT_STATUS_LABEL_KEY[status];
  return key ? t(key) : status;
};

export interface StatusChip {
  variant: BadgeVariant;
  label: string;
}

export const getStatusChip = (status: BidRequestStatus, t: TFunction): StatusChip => ({
  variant: getStatusBadgeVariant(status),
  label: getStatusLabel(status, t),
});

export const getFulfilmentChip = (status: FulfilmentStatus, t: TFunction): StatusChip => ({
  variant: getFulfilmentBadgeVariant(status),
  label: getFulfilmentLabel(status, t),
});

// DRF DecimalField serializes as a zero-padded string ("50.00000"); show it as a
// plain number ("50", "10.5") with an optional unit suffix.
export const formatQuantity = (value: string | number, unitCode?: string): string => {
  const numeric = Number(value);
  const quantity = Number.isFinite(numeric) ? String(numeric) : String(value);
  return unitCode ? `${quantity} ${unitCode}` : quantity;
};

export const isSettled = (settlement: Pick<Settlement, "outstanding">): boolean =>
  Number(settlement.outstanding) <= 0;

// Before delivery can start the farmer organisation must accept the demand and
// then assign stock (deposits) to every commodity line.
export const isAwaitingStockOrAcceptance = (status?: FulfilmentStatus | null): boolean =>
  status === FULFILMENT_STATUS.awaitingAcceptance || status === FULFILMENT_STATUS.awaitingStock;

export interface StockProgress {
  linked: string;
  agreed: string;
  complete: boolean;
}

// Stock assigned to one commodity line vs its agreed quantity, or null when the
// backend did not send the stock block (older payload).
export const getStockProgress = (
  commodity: SettlementCommodity,
  unitCode?: string
): StockProgress | null => {
  if (commodity.linked_quantity === undefined) return null;
  return {
    linked: formatQuantity(commodity.linked_quantity, unitCode),
    agreed: formatQuantity(commodity.agreed_quantity, unitCode),
    complete: Boolean(commodity.stock_complete),
  };
};

// `unit_of_measure` id -> unit code, taken from the request's own commodity lines,
// so the settlement rollup (which carries only unit ids) can render "50 kg".
export const buildUnitCodeByUnitId = (items: BidRequestItem[]): Record<number, string> =>
  items.reduce<Record<number, string>>((acc, item) => {
    acc[item.unit_of_measure] = item.unit_of_measure_code;
    return acc;
  }, {});

export interface DeliveryLineView {
  childOfferId: number;
  commodityName: string;
  delivered: string;
  remaining: string | null;
}

export interface DeliveryView {
  uuid: string;
  deliveredAt: string;
  attachment: string | null;
  comment: string;
  lines: DeliveryLineView[];
}

// Turn the raw deliveries (newest-first from the API) into display cards where each
// line shows what that delivery brought plus the commodity's remaining *after* it.
// Remaining is computed by replaying deliveries oldest-first and accumulating.
export const buildDeliveryViews = (
  settlement: Settlement,
  unitCodeByUnitId: Record<number, string>
): DeliveryView[] => {
  const agreedByChild = new Map<number, number>();
  const unitByChild = new Map<number, number>();
  settlement.commodities.forEach(commodity => {
    agreedByChild.set(commodity.child_offer_id, Number(commodity.agreed_quantity));
    unitByChild.set(commodity.child_offer_id, commodity.unit_of_measure_id);
  });

  const cumulative = new Map<number, number>();
  const oldestFirst = [...settlement.deliveries].reverse();

  const viewsOldestFirst = oldestFirst.map(delivery => {
    const lines = delivery.items.map(item => {
      const runningTotal = (cumulative.get(item.child_offer) ?? 0) + Number(item.quantity);
      cumulative.set(item.child_offer, runningTotal);

      const agreed = agreedByChild.get(item.child_offer) ?? 0;
      const remainingValue = Math.max(agreed - runningTotal, 0);
      const unitCode =
        unitCodeByUnitId[unitByChild.get(item.child_offer) ?? item.unit_of_measure_id];

      return {
        childOfferId: item.child_offer,
        commodityName: item.commodity_name,
        delivered: formatQuantity(item.quantity, unitCode),
        remaining: remainingValue > 0 ? formatQuantity(remainingValue, unitCode) : null,
      };
    });

    return {
      uuid: delivery.uuid,
      deliveredAt: delivery.delivered_at,
      attachment: delivery.attachment,
      comment: delivery.comment,
      lines,
    };
  });

  return viewsOldestFirst.reverse();
};

interface PaymentMethodLike {
  id: number | string;
  name: string;
  translations?: Record<string, string>;
}

export const getPaymentMethodName = (
  id: number | string,
  methods: PaymentMethodLike[],
  language?: string
): string => {
  const method = methods.find(candidate => String(candidate.id) === String(id));
  if (!method) {
    return "";
  }
  const translated = language ? method.translations?.[language] : undefined;
  return translated || method.name || "";
};
